// FLAG:useDocuAi — which files the receipt upload accepts, and what the user is told about the
// ones it refuses.
//
// QA TC-1505: an unsupported file was dropped SILENTLY. The list dropzone filtered it out
// before the upload started, so the upload reported success for the remaining files (or for
// none at all) and the refused file simply never appeared — no message, no trace. The legacy
// FineUploader path did tell the user (`lang.fileUploaderTypesError`); the DMS→DocuAI path
// inherited the filter but not the message.
//
// The rule lives here so both entry points — the expense/revenue list dropzone and the
// receipt form (New Expense / New Revenue) — answer identically. They previously each had
// their own copy of the extension list, in different casing.

/** Lower-case, compared against a lower-cased file extension. Never shown to the user. */
export const RECEIPT_UPLOAD_EXTENSIONS = ['jpeg', 'jpg', 'png', 'pdf', 'xml', 'zugferd']

/**
 * The same list, but with the spelling used for DISPLAY — „ZUGFeRD" is a product name, not a file
 * extension. Kept separate from the comparison list
 * because F13 (comparison failed while 'ZUGFeRD'
 * used mixed case) correctly changed matching to lower case — but that lower-case spelling then
 * leaked into the DISPLAY: the expense/revenue dropzone showed „zugferd", while the receipt form
 * next to it shows „ZUGFeRD". Order and length must match RECEIPT_UPLOAD_EXTENSIONS.
 */
export const RECEIPT_UPLOAD_EXTENSION_LABELS = ['jpeg', 'jpg', 'png', 'pdf', 'xml', 'ZUGFeRD']

export const getFileExtension = (fileName: string): string =>
  (fileName || '').split('.').pop()?.toLowerCase() ?? ''

export const isAllowedReceiptUploadFile = (file: File): boolean =>
  RECEIPT_UPLOAD_EXTENSIONS.includes(getFileExtension(file?.name ?? ''))

export interface ReceiptUploadPartition {
  accepted: File[]
  rejected: File[]
}

/**
 * Split a drop into the files that will be uploaded and the ones that will not.
 *
 * The `accepted` side is byte-for-byte the set the previous inline filter produced — the
 * refused files were never the problem, their silence was.
 */
export const partitionReceiptUploadFiles = (files: File[]): ReceiptUploadPartition => {
  const accepted: File[] = []
  const rejected: File[] = []
  for (const file of files || []) {
    if (!file) { continue }
    (isAllowedReceiptUploadFile(file) ? accepted : rejected).push(file)
  }
  return { accepted, rejected }
}

/**
 * The message for refused files — names them, so a mixed drop of ten files says WHICH one was
 * refused rather than leaving the user to count rows. Returns null when nothing was refused,
 * so callers can `if (message)` without a second length check.
 */
export const getUnsupportedReceiptFilesMessage = (
  rejected: File[],
  lang: {
    unsupportedFileType: (fileName: string, formats: string) => string,
    unsupportedFileTypes: (fileNames: string, formats: string) => string,
  },
): string | null => {
  if (!rejected?.length) { return null }
  const formats = RECEIPT_UPLOAD_EXTENSION_LABELS.join(', ')
  const names = rejected.map(file => file.name)
  return names.length === 1
    ? lang.unsupportedFileType(names[0], formats)
    : lang.unsupportedFileTypes(names.join(', '), formats)
}
